import { collection, doc, setDoc } from 'firebase/firestore';
import { db } from './config';

// Legacy device types removed - using only DeviceType enum now

// New device types for wallpaper packs
export enum DeviceType {
  MOBILE_PHONE = 'mobile_phone',
  IPHONE = 'iphone',
  IPHONE_PRO = 'iphone_pro',
  ANDROID = 'android',
  TABLET = 'tablet',
  LAPTOP = 'laptop',
  DESKTOP = 'desktop',
  ULTRAWIDE = 'ultrawide',
  TV = 'tv',
  WATCH = 'watch'
}

// Type definitions for our Firestore schema
export interface Wallpaper {
  id: string;
  title: string;
  description?: string;
  category?: string; // Legacy field, keeping for backward compatibility
  categoryId?: string; // New field for category reference
  tags: string[];
  imageUrl: string;
  thumbnailUrl: string;
  resolutions: Resolution[];
  downloadCount: number;
  likes?: string[]; // Array of user IDs who liked this wallpaper
  uploadedBy?: string; // ID of the admin user who uploaded this wallpaper
  createdAt: Date;
  updatedAt: Date;
  searchKeywords: string[]; // for search optimization

  // Live wallpaper fields
  isVideo?: boolean;
  videoUrl?: string;
  videoThumbnail?: string;
  videoDuration?: number;
  videoAspectRatio?: string;
  isAnimated?: boolean;

  // New fields for device wallpaper packs
  isWallpaperPack?: boolean;
  deviceVariations?: {
    [key in DeviceType]?: {
      imageUrl: string;
      thumbnailUrl: string;
      resolutions: Resolution[];
      dimensions: {
        width: number;
        height: number;
      };
      validationScore?: number;
    }
  };
  // Enhanced format supporting multiple variations per device
  deviceVariationsList?: {
    device: DeviceType;
    variations: {
      id: string;
      imageUrl: string;
      thumbnailUrl?: string;
      resolutions?: Resolution[];
      dimensions: {
        width: number;
        height: number;
      };
      validationScore?: number;
      isPrimary: boolean;
    }[];
  }[];
  supportedDevices?: DeviceType[];
  primaryDevice?: DeviceType;
  packMetadata?: {
    totalVariations: number;
    createdAsType: 'single' | 'pack';
    lastVariationUpdate: Date;
  };
}

export interface Resolution {
  width: number;
  height: number;
  url: string;
  size: string; // e.g., "1920x1080", "mobile", "tablet"
}

export interface User {
  id: string;
  displayName?: string | null;
  email?: string | null;
  photoURL?: string | null;
  preferences: UserPreferences;
  role: UserRole;
  createdAt?: Date;
  lastActive?: Date;
  [key: string]: unknown; // Allow additional properties
}

export enum UserRole {
  USER = 'user',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin'
}

// Types for admin authentication
export interface AdminLoginCredentials {
  email: string;
  password: string;
  adminCode?: string;
}

export interface AdminAuthResult {
  user: AdminUser;
  token: string;
}

export interface AdminUser extends User {
  // Required fields
  userId: string;
  role: UserRole.ADMIN | UserRole.SUPER_ADMIN;
  permissions: AdminPermission[];

  // Optional fields
  email?: string;
  displayName?: string | null;
  photoURL?: string | null;

  // Timestamps
  createdAt?: Date;
  updatedAt?: Date;
  lastAdminAction?: Date;
  promotedAt?: Date;

  // Additional metadata
  promotedBy?: string;
  system_initialized?: boolean;

  // Backward compatibility
  id: string;

  // Allow additional properties
  [key: string]: unknown;
}

export enum AdminPermission {
  MANAGE_WALLPAPERS = 'manage_wallpapers',
  MANAGE_USERS = 'manage_users',
  MANAGE_CATEGORIES = 'manage_categories',
  VIEW_ANALYTICS = 'view_analytics',
  MANAGE_ADMINS = 'manage_admins',
  SYSTEM_CONFIG = 'system_config'
}

export interface UserPreferences {
  preferredResolution: string;
  adConsent: boolean;
  theme: 'light' | 'dark';
  // GDPR compliance fields
  gdprConsent?: {
    hasConsented: boolean;
    consentDate: Date | null;
    consentVersion: string;
    analytics: boolean;
    advertising: boolean;
    functional: boolean;
  };
}

export interface UserFavorite {
  id: string; // wallpaper ID
  addedAt: Date;
}

export interface UserDownload {
  id: string;
  wallpaperId: string;
  resolution: string;
  downloadedAt: Date;
}

// Category interface
export interface Category {
  id: string;
  name: string;
  nameLower: string; // For case-insensitive search
  description: string;
  createdAt: Date;
  updatedAt: Date;
  wallpaperCount: number;
}

// Device specification interface for Firestore
export interface DeviceSpecificationDocument {
  id: string; // DeviceType enum value
  name: string;
  displayName: string;
  icon: string;
  category: string; // 'mobile', 'tablet', 'desktop'
  aspectRatios: {
    ratio: string;
    description: string;
    isCommon: boolean;
    width: number;
    height: number;
  }[];
  recommendedResolutions: {
    width: number;
    height: number;
    label: string;
    isOptimal: boolean;
  }[];
  mockupFrames: {
    name: string;
    frameUrl: string;
    screenBounds: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
  }[];
  validationRules: {
    minWidth: number;
    minHeight: number;
    maxFileSize: number;
    supportedFormats: string[];
  };
}

// Collection references
export const wallpapersCollection = collection(db, 'wallpapers');
export const usersCollection = collection(db, 'users');
export const adminCollection = collection(db, 'admin');
export const categoriesCollection = collection(db, 'categories');
export const deviceSpecificationsCollection = collection(db, 'deviceSpecifications');
export const blogPostsCollection = collection(db, 'blogPosts');
export const blogCategoriesCollection = collection(db, 'blogCategories');
export const blogCommentsCollection = collection(db, 'blogComments');

// Helper function to get user favorites subcollection
export const getUserFavoritesCollection = (userId: string) =>
  collection(db, 'users', userId, 'favorites');

// Helper function to get user downloads subcollection
export const getUserDownloadsCollection = (userId: string) =>
  collection(db, 'users', userId, 'downloads');

// Sample data structure for wallpapers collection
export const createSampleWallpaper = async (wallpaperData: Omit<Wallpaper, 'id'>): Promise<void> => {
  try {
    const wallpaperRef = doc(wallpapersCollection);
    const wallpaperWithId: Wallpaper = {
      ...wallpaperData,
      id: wallpaperRef.id,
    };

    await setDoc(wallpaperRef, wallpaperWithId);
    console.log('Sample wallpaper created with ID:', wallpaperRef.id);
  } catch (error) {
    console.error('Error creating sample wallpaper:', error);
    throw error;
  }
};

// Sample data structure for users collection
export const createUserProfile = async (
  userId: string,
  userData: {
    displayName?: string | null;
    email?: string | null;
    photoURL?: string | null;
    preferences?: Partial<UserPreferences>;
    role?: UserRole;
    createdAt?: Date;
    lastActive?: Date;
  } = {}
): Promise<void> => {
  try {
    const userRef = doc(usersCollection, userId);

    // Default preferences
    const defaultPreferences: UserPreferences = {
      preferredResolution: '1920x1080',
      adConsent: false,
      theme: 'light'
    };

    // Merge provided preferences with defaults
    const mergedPreferences: UserPreferences = {
      ...defaultPreferences,
      ...userData.preferences
    };

    // Create user with all required fields
    const userWithId: User = {
      id: userId,
      displayName: userData.displayName || null,
      email: userData.email || null,
      photoURL: userData.photoURL || null,
      preferences: mergedPreferences,
      role: userData.role || UserRole.USER,
      createdAt: userData.createdAt || new Date(),
      lastActive: userData.lastActive || new Date()
    };

    await setDoc(userRef, userWithId);
    console.log('User profile created with ID:', userId);
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

// Helper function to create device specification documents
export const createDeviceSpecification = async (
  deviceType: DeviceType,
  specData: Omit<DeviceSpecificationDocument, 'id'>
): Promise<void> => {
  try {
    const specRef = doc(deviceSpecificationsCollection, deviceType);
    const specWithId: DeviceSpecificationDocument = {
      ...specData,
      id: deviceType,
    };

    await setDoc(specRef, specWithId);
    console.log('Device specification created for:', deviceType);
  } catch (error) {
    console.error('Error creating device specification:', error);
    throw error;
  }
};